import { useEffect, useMemo, useState } from "react";
import { Link as LinkDom, useLocation } from "react-router-dom";
import { TriangleAlert, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { auth } from "../api/firebaseApi";
import { firmConfiguration } from "../constans";
import { getSubdomain, setupIntercom } from "../helpers/helpers";
import { useGetUserDetails } from "../hooks/useGetUserDetails";
import styles from "../styles/header.module.css";
import { NavBarLogo } from "./NavbarLogo";
import { UserMenu } from "./UserMenu";
import useAdminUpdates from "../hooks/admin/useManageAdminUpdates";
import { AdminUpdate } from "../pages/Admin/Updates/AdminUpdate";
import {
  CLOSED_UPDATES_EVENT,
  closeUpdate,
  readClosedUpdateIds
} from "../helpers/closedCriticalUpdates";

interface HeaderProps {
  link?: string;
}

export const Header = ({ link }: HeaderProps) => {
  const location = useLocation();

  const {
    userDetails: { id, userType, email }
  } = useGetUserDetails();
  const { adminUpdates, fetchAdminUpdates } = useAdminUpdates();

  const [closedUpdates, setClosedUpdates] = useState<string[]>(() => {
    return readClosedUpdateIds(id);
  });

  const isIndividual = userType === "Individual";
  const isForeignNational = userType === "ForeignNational";

  const [isAuthChecked, setIsAuthChecked] = useState(false);

  useEffect(() => {
    setupIntercom(email, id);
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && location.pathname !== "/login") setIsAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  const logoLink = useMemo(() => {
    if (link) {
      return link;
    }
    if (isForeignNational) {
      return "/portal";
    }
    if (!isIndividual) {
      return "/cases";
    }
    return "/";
  }, [id, isForeignNational, isIndividual, link]);

  const subdomain = getSubdomain();
  const firmConfig = firmConfiguration[subdomain];

  const hideTopNavMenuItems = location.pathname === "/accept-agreement";

  // Filter out updates that have been closed
  const criticalUpdates = adminUpdates.filter(
    (update: AdminUpdate) =>
      update.priority === "critical" &&
      !closedUpdates.includes(update?.id || "")
  );

  // We'll only show the first available critical update
  const currentCriticalUpdate =
    criticalUpdates.length > 0 ? criticalUpdates[0] : null;

  const handleCloseUpdate = (updateId?: string) => {
    if (!updateId) return;

    closeUpdate(updateId, id);
  };

  useEffect(() => {
    fetchAdminUpdates();
  }, []);

  useEffect(() => {
    const handler = () => setClosedUpdates(readClosedUpdateIds(id));
    handler();
    window.addEventListener(CLOSED_UPDATES_EVENT, handler);
    return () => window.removeEventListener(CLOSED_UPDATES_EVENT, handler);
  }, [id]);

  return (
    <header className={styles.header} style={firmConfig?.cssForHeader}>
      {currentCriticalUpdate && (
        <div
          role="alert"
          className="flex w-full items-center gap-3 bg-danger-soft px-4 py-2"
        >
          <TriangleAlert className="size-4 shrink-0 text-danger" />
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-base font-medium text-danger">
              {currentCriticalUpdate.title}
            </span>
            {currentCriticalUpdate.description ? (
              <span className="text-sm text-fg-2">
                {currentCriticalUpdate.description}
              </span>
            ) : null}
          </div>
          <Button
            variant="danger"
            size="icon-xs"
            aria-label="Close update"
            onClick={() => handleCloseUpdate(currentCriticalUpdate?.id)}
          >
            <X />
          </Button>
        </div>
      )}
      <div className="flex items-center py-2">
        <div className="ml-4.5 flex items-center">
          <span className="font-medium">
            <LinkDom to={logoLink} className="m-0 p-0" />
          </span>
          <div>
            <LinkDom
              to={firmConfig?.domainUrl || logoLink}
              target={firmConfig?.domainUrl ? "_blank" : "_self"}
              className="m-0 flex items-center p-0"
            >
              <NavBarLogo />
            </LinkDom>
          </div>
        </div>
        <div className="flex-1" />
        <div className="flex items-center px-4">
          {isAuthChecked && !hideTopNavMenuItems && (
            <div className="flex max-h-8 items-center gap-4">
              <UserMenu />
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
